'use client';

import { useState } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Typography, Stack, Button, Chip, Link } from '@mui/material';
import { ImageWithFallback } from '@qb/frontend/components/ImageWithFallback';
import { Rotate } from '@qb/frontend/components/Rotate';
import { useIsOverBreakpoint } from '@qb/frontend/hooks/mediaQueries';
import { clampLinesSx } from '@qb/frontend/utils/clampLinesSx';
import { getFirstImage } from '@qb/frontend/utils/getFirstImage';
import { truncateWithEllipsis } from '@qb/frontend/utils/truncateWithEllipsis';
import { BaseCarousel } from '@/components/Common/BaseCarousel/BaseCarousel';
import { calculateDefaultPurchaseQuantityValue } from '@/global/pages/PartDisplayPage/_utils/calculateDefaultPurchaseQuantityValue';
import { PartDisplayDataResponse, PartGetComparePartsResponse } from '@/shared/types/controllers/sps/SPSPartControllerTypes';
import { generatePartURL } from '@/shared/utils/URLs';
const formatFeatureInstances = (featureInstances: PartGetComparePartsResponse['parts'][0]['featureInstances']) => {
  return featureInstances.map(({
    feature,
    value
  }) => ({
    id: feature.category.id,
    key: feature.category.name,
    value: `${value} ${feature.unit ?? ''}`
  }));
};
const getFeaturesCategories = (parts: Array<CompareProductsCarouselProps['partsToCompare'][0] | CompareProductsCarouselProps['selectedPart']>) => {
  return parts.map(part => formatFeatureInstances(part.featureInstances).concat(part.basicParametricDatas)).flat().filter((value, index, self) => self.findIndex(v => v.key === value.key) === index).map(({
    value: _value,
    ...rest
  }) => rest);
};
const cellStyle = {
  py: 1.125,
  px: [1, 2],
  whiteSpace: 'nowrap',
  color: 'text.primary',
  '&:nth-child(even)': {
    backgroundColor: 'rgba(236, 234, 238, 0.73)'
  }
};
const HeadingColumn = ({
  featuresCategories
}: Pick<ComparedProductProps, 'featuresCategories'>) => <Box sx={{
  display: {
    xs: 'none',
    xl: 'block'
  }
}} data-sentry-element="Box" data-sentry-component="HeadingColumn" data-sentry-source-file="CompareProductsCarousel.tsx">
    {featuresCategories.map(({
    key,
    id
  }) => <Typography key={id} variant="bodyMediumPrimary" sx={cellStyle} title={key}>
        {truncateWithEllipsis(key, 25)}
      </Typography>)}
  </Box>;
type ComparedProductProps = {
  tenantAlias: string;
  part: CompareProductsCarouselProps['selectedPart'] | PartGetComparePartsResponse['parts'][0];
  isSelectedProduct: boolean;
  featuresCategories: {
    id: number;
    key: string;
  }[];
};
const ComparedProduct = ({
  isSelectedProduct,
  part,
  tenantAlias,
  featuresCategories
}: ComparedProductProps) => {
  const combinedStock = part.publicQtyAndPrice.onHandQty + part.publicQtyAndPrice.manufacturerStock;
  const isMarketablePart = combinedStock > 0 && !!part.publicQtyAndPrice.price;
  const formattedFeatureInstances = formatFeatureInstances(part.featureInstances).concat(part.basicParametricDatas);
  const mappedValuesToCategories = featuresCategories.map(category => {
    const feature = formattedFeatureInstances.find(feature => feature.key === category.key);
    return feature ?? {
      ...category,
      value: '-'
    };
  });
  return <Box sx={{
    flexGrow: 1,
    flexBasis: 0,
    border: '1px solid transparent',
    borderColor: isSelectedProduct ? null : 'grey.300',
    backgroundColor: isSelectedProduct ? 'common.backgroundColor' : null
  }} data-sentry-element="Box" data-sentry-component="ComparedProduct" data-sentry-source-file="CompareProductsCarousel.tsx">
      {isSelectedProduct && <Typography variant="bodyMediumPrimary" sx={{
      bgcolor: 'primary.main',
      color: 'primary.contrastText',
      py: 1,
      px: 2
    }}>
          Selected product
        </Typography>}
      <Box sx={{
      px: cellStyle.px,
      pt: 1.5,
      pb: 1.5
    }} data-sentry-element="Box" data-sentry-source-file="CompareProductsCarousel.tsx">
        <Stack spacing={1} sx={{
        mb: 1,
        justifyContent: 'space-between',
        flexDirection: ['column', 'row']
      }} data-sentry-element="Stack" data-sentry-source-file="CompareProductsCarousel.tsx">
          <ImageWithFallback tenantAlias={tenantAlias} src={getFirstImage(part.images)} alt={part.partNumber} width={70} height={70} data-sentry-element="ImageWithFallback" data-sentry-source-file="CompareProductsCarousel.tsx" />
          <Chip size="small" icon={<CheckCircleIcon />} label={!!combinedStock ? `${combinedStock} in stock` : `Backordered`} sx={{
          bgcolor: !!combinedStock ? '#00ED8E' : 'warning.main',
          color: 'text.primary',
          '.MuiChip-icon': {
            color: 'text.primary'
          }
        }} data-sentry-element="Chip" data-sentry-source-file="CompareProductsCarousel.tsx" />
        </Stack>
        <Link title={part.partNumber} target="_blank" href={generatePartURL(part.manufacturer.name, part.partNumber, part.id)} data-sentry-element="Link" data-sentry-source-file="CompareProductsCarousel.tsx">
          <Typography variant="h6" data-sentry-element="Typography" data-sentry-source-file="CompareProductsCarousel.tsx">{part.partNumber}</Typography>
        </Link>

        <Typography variant="h6" data-sentry-element="Typography" data-sentry-source-file="CompareProductsCarousel.tsx">{part?.manufacturer?.name}</Typography>
        {isMarketablePart ? <Button fullWidth color="primary" variant="contained" sx={{
        borderRadius: 100,
        mt: 1.5
      }} onClick={() => {
        window?.salesPilotPostMessages?.addToCart({
          qty: calculateDefaultPurchaseQuantityValue({
            onHandQty: part.publicQtyAndPrice.onHandQty,
            minimumOrderQty: part?.publicQtyAndPrice?.minimumOrderQty || 1
          }),
          partID: part.id
        });
      }}>
            Add to cart
          </Button> : <Button fullWidth variant="contained" sx={{
        borderRadius: 100,
        mt: 1.5
      }} onClick={() => {
        window?.salesPilotPostMessages?.createQuote({
          qty: 1,
          partID: part.id
        });
      }}>
            Request Quote
          </Button>}
        <Typography title={part.description} variant="paragraph" sx={[{
        mt: 1,
        height: 63
      }, clampLinesSx(3)]} data-sentry-element="Typography" data-sentry-source-file="CompareProductsCarousel.tsx">
          {truncateWithEllipsis(part.description, 100)}
        </Typography>
      </Box>
      <Box data-sentry-element="Box" data-sentry-source-file="CompareProductsCarousel.tsx">
        {mappedValuesToCategories.map(({
        key,
        id,
        value
      }) => <Typography key={id} variant="bodyMedium" sx={cellStyle} title={value}>
            <Typography component="span" sx={{
          display: {
            xs: 'block',
            xl: 'none'
          },
          fontSize: 10,
          fontWeight: 600
        }}>
              {key}
            </Typography>
            {truncateWithEllipsis(value, 28)}
          </Typography>)}
      </Box>
    </Box>;
};
type CompareProductsCarouselProps = {
  selectedPart: PartDisplayDataResponse['part'] & Pick<PartGetComparePartsResponse['parts'][0], 'publicQtyAndPrice' | 'basicParametricDatas' | 'featureInstances'>;
  partsToCompare: PartGetComparePartsResponse['parts'];
  tenantAlias: string;
  partCategoryName?: string;
};
const EXPAND_MIN_LENGTH = 10;
export const CompareProductsCarousel = ({
  selectedPart,
  partsToCompare,
  tenantAlias,
  partCategoryName
}: CompareProductsCarouselProps) => {
  const isDesktop = useIsOverBreakpoint('lg');
  const [isExpanded, setIsExpanded] = useState(false);
  if (!partsToCompare?.length) {
    return null;
  }
  const trimmedPartsToCompare = (partsToCompare || []).filter(part => part.id !== selectedPart.id).slice(0, 3);
  const allFeaturesCategories = getFeaturesCategories([selectedPart, ...trimmedPartsToCompare]);
  const featuresCategories = isExpanded || allFeaturesCategories.length < EXPAND_MIN_LENGTH ? allFeaturesCategories : allFeaturesCategories.slice(0, EXPAND_MIN_LENGTH);
  const isUsingCarousel = !(trimmedPartsToCompare.length === 1 || isDesktop);
  const partsComponents = trimmedPartsToCompare.map(part => <ComparedProduct key={part.id} tenantAlias={tenantAlias} part={part} isSelectedProduct={false} featuresCategories={featuresCategories} />);
  const renderCarousel = () => {
    if (!isUsingCarousel) {
      return partsComponents;
    }
    const slidesToShow = {
      xs: 1,
      sm: 1,
      md: trimmedPartsToCompare.length > 1 ? 2 : 1,
      lg: 2,
      xl: 2
    };

    // Not desktop with multiple items
    return <Box sx={{
      // grow the slider according to the number of items in the slider
      flexGrow: slidesToShow,
      flexBasis: 0,
      '& .slick-slide': {
        px: [1, 2]
      },
      '& .slick-list': {
        mx: [-1, -2, -2, -2, -2]
      }
    }} data-sentry-element="Box" data-sentry-component="renderCarousel" data-sentry-source-file="CompareProductsCarousel.tsx">
        <BaseCarousel slidesCount={trimmedPartsToCompare.length} slidesToShow={slidesToShow} data-sentry-element="BaseCarousel" data-sentry-source-file="CompareProductsCarousel.tsx">
          {partsComponents}
        </BaseCarousel>
      </Box>;
  };
  return <Box sx={{
    py: 4
  }} data-sentry-element="Box" data-sentry-component="CompareProductsCarousel" data-sentry-source-file="CompareProductsCarousel.tsx">
      <Typography variant="h4" component="h3" sx={{
      mb: 4
    }} data-sentry-element="Typography" data-sentry-source-file="CompareProductsCarousel.tsx">
        Compare {selectedPart.partNumber} with other {partCategoryName} products
      </Typography>

      <Stack direction="row" sx={{
      gap: [1, 2, 3, 4],
      alignItems: 'end'
    }} data-sentry-element="Stack" data-sentry-source-file="CompareProductsCarousel.tsx">
        <HeadingColumn featuresCategories={featuresCategories} data-sentry-element="HeadingColumn" data-sentry-source-file="CompareProductsCarousel.tsx" />
        <ComparedProduct tenantAlias={tenantAlias} part={selectedPart} isSelectedProduct={true} featuresCategories={featuresCategories} data-sentry-element="ComparedProduct" data-sentry-source-file="CompareProductsCarousel.tsx" />
        {renderCarousel()}
      </Stack>
      {allFeaturesCategories.length > EXPAND_MIN_LENGTH && <Button onClick={() => setIsExpanded(prev => !prev)} variant="text" color="primary" sx={{
      display: 'flex',
      mx: 'auto',
      mt: 1
    }} endIcon={<Rotate isRotated={isExpanded}>
              <ExpandMoreIcon />
            </Rotate>}>
          {`See ${isExpanded ? 'less' : 'more'} details.`}
        </Button>}
    </Box>;
};